/* @flow */

import type { GamestreamToken } from '../../../helpers/gaming/types';
import type { BasicFunction, KeyValuePair } from '../../../helpers/jsHelpers/types';
import type { ParentalControlItemState } from '../../../helpers/parentalControl/constsAndTypes';
import type { PENDING_OPERATION_DATA_TYPE, VOD_PURCHASE_DATA_TYPE } from '../../../helpers/rights/pendingOperations';
import type { BroadcastStatus } from '../../../helpers/ui/location/Format';
import type { STREAM_PRIORITIES_TYPE, VOD_STATUS_TYPE } from '../../../helpers/ui/metadata/Types';
import type { ThemeType } from '../../../helpers/ui/theme';
import type { ChannelMap } from '../../../libs/netgemLibrary/v8/types/Channel';
import type { MetadataKind, NETGEM_API_V8_METADATA, NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_RECORDINGS_MAP, NETGEM_SCHEDULED_RECORDINGS_MAP, ScheduledRecordingsKindType } from '../../../libs/netgemLibrary/v8/types/Npvr';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_PLAYINGHISTORY_ITEM, PLAYING_HISTORY_ARRAY_TYPE } from '../../../libs/netgemLibrary/v8/types/PlayingHistory';
import type { NETGEM_API_V8_PURCHASE_INFO } from '../../../libs/netgemLibrary/v8/types/PurchaseInfo';
import type { NETGEM_API_V8_AUTHENT_REALM } from '../../../libs/netgemLibrary/v8/types/Realm';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { NETGEM_API_V8_RIGHTS } from '../../../libs/netgemLibrary/v8/types/Rights';
import type { NETGEM_API_VIEWINGHISTORY } from '../../../libs/netgemLibrary/v8/types/ViewingHistory';
import type { AppConfigurationState } from '../../../redux/appConf/reducers';
import type { ModalState } from '../../../redux/modal/reducers';
import type { BO_FAVORITE_LIST_TYPE } from '../../../redux/netgemApi/actions/videofutur/types/favorite';
import type { BO_PURCHASE_LIST_TYPE } from '../../../redux/netgemApi/actions/videofutur/types/purchase';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../confirmationModal/ConfirmationModal';
import type { EULA_DATA_MODAL_TYPE } from '../eulaModal/EulaModal';
import type { GAME_PROFILE_SELECT_DATA_MODAL_TYPE } from '../gameProfileSelectModal/GameProfileSelectModal';
import type { MESSAGE_MODAL_TYPE } from '../messageModal/MessageModal';
import type { PIN_DATA_MODAL_TYPE } from '../pinModal/PinModalConstsAndTypes';

import { LoadableStatus } from '../../../helpers/loadable/loadable';
import ContinuousWatching from '../../../helpers/viewingHistory/ContinuousWatching';
import { ItemContent, type NETGEM_API_V8_FEED_ITEM, type NETGEM_API_V8_ITEM_LOCATION, type NetgemApiV8ItemLocation } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { TileConfigTileType } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';

// Time display and broadcast status are refreshed every second (in ms)
export const REFRESH_TIMEOUT = 1_000;

// Time to wait before trying again to perform the pending play operation (in ms)
export const UNINITIALIZED_LOCK_STATE_DELAY = 100;

export enum ImageDisplayType {
  Landscape,
  Portrait,
  Unset,
}

export type CARD_DATA_MODAL_TYPE = {
  displayedSectionCount?: number | null,
  distributorId?: string | null,
  episodeIndex?: number,
  isOpenFromPlayer?: boolean,
  isTimeshifting?: boolean,
  isUnlockedAdultContent?: boolean,
  item: NETGEM_API_V8_FEED_ITEM,
  locationType?: NetgemApiV8ItemLocation | null,
  pendingOperation?: PENDING_OPERATION_DATA_TYPE | null,
  previousCard?: CARD_DATA_MODAL_TYPE | null,
  programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  recordKind?: ScheduledRecordingsKindType | null,
  seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  tileType: TileConfigTileType,
  urlDefinition?: NETGEM_API_V8_URL_DEFINITION | null,
  viewingHistoryId?: string | null,
  vtiId?: number | null,
};

export type ReduxCardModalDispatchToPropsType = {
  +localAddToFavoriteList: (titId: string, signal?: AbortSignal) => Promise<void>,
  +localAddToWishlist: (assetId: string, channelId: string, signal?: AbortSignal) => Promise<void>,
  +localGetGamestreamToken: (item: NETGEM_API_V8_FEED_ITEM, signal?: AbortSignal) => Promise<GamestreamToken>,
  +localGetImageSourceUrl: (assetId: string, width: number, height: number) => Promise<string>,
  +localGetImageUrl: (assetId: string, width: number, height: number, theme?: ThemeType, signal?: AbortSignal) => Promise<string>,
  +localGetPurchaseInfoPerAsset: (id: string, channelId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_PURCHASE_INFO>,
  +localGetVodLocations: (locations: Array<NETGEM_API_V8_ITEM_LOCATION>, signal?: AbortSignal) => Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE>>,
  +localHideModal: (shouldBeReopenedLater?: boolean) => void,
  +localRemoveFromFavoriteList: (titId: string, signal?: AbortSignal) => Promise<void>,
  +localRemoveFromWishlist: (assetId: string, channelId: string, signal?: AbortSignal) => Promise<void>,
  +localReopenModalCard: BasicFunction,
  +localResetSectionPageIndices: (prefix?: string) => void,
  +localSendV8LocationCatchupForAssetRequest: (assetId: string, startDate: number, range: number, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localSendV8MetadataLocationRequest: (assetId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localSendV8MetadataRequest: (assetId: string, type: MetadataKind, signal?: AbortSignal) => Promise<NETGEM_API_V8_METADATA>,
  +localSendV8RecordingsMetadataRequest: (recordId: string, signal?: AbortSignal) => Promise<NETGEM_API_V8_REQUEST_RESPONSE>,
  +localShowConfirmation: (data: CONFIRMATION_DATA_MODAL_TYPE, ariaLabel: string) => void,
  +localShowEula: (data: EULA_DATA_MODAL_TYPE) => void,
  +localShowGameProfileSelect: (data: GAME_PROFILE_SELECT_DATA_MODAL_TYPE) => void,
  +localShowMessage: (messageData: MESSAGE_MODAL_TYPE, ariaLabel?: string) => void,
  +localShowPinModal: (data: PIN_DATA_MODAL_TYPE) => void,
  +localUpdatePlayingHistory: (playingHistoryItem: NETGEM_API_PLAYINGHISTORY_ITEM, itemId: string) => Promise<void>,
  +showVodPurchaseDialog: (vodPurchaseData: VOD_PURCHASE_DATA_TYPE) => void,
};

export type ReduxCardModalReducerStateType = {
  +appConfiguration: AppConfigurationState,
  +applicationName: string,
  +channels: ChannelMap,
  +commercialOffers: KeyValuePair<Array<string>> | null,
  +deeplink: string | null,
  +defaultRights: NETGEM_API_V8_RIGHTS | null,
  +favoriteList: BO_FAVORITE_LIST_TYPE,
  +isDebugModeEnabled: boolean,
  +isNpvrEnabled: boolean,
  +isRegisteredAsGuest: boolean,
  +isSubscriptionFeatureEnabled: boolean,
  +npvrRecordingsFuture: NETGEM_RECORDINGS_MAP,
  +npvrRecordingsList: NETGEM_RECORDINGS_MAP,
  +npvrScheduledRecordingsList: NETGEM_SCHEDULED_RECORDINGS_MAP,
  +playingHistory: PLAYING_HISTORY_ARRAY_TYPE,
  +playingHistoryStatus: LoadableStatus,
  +purchaseList: BO_PURCHASE_LIST_TYPE | null,
  +streamPriorities: STREAM_PRIORITIES_TYPE | null,
  +userRights: Array<string> | null,
  +viewingHistory: NETGEM_API_VIEWINGHISTORY,
  +viewingHistoryStatus: LoadableStatus,
  +wishlist: Set<string>,
  +wishlistStatus: LoadableStatus,
};

export type CompleteCardModalPropType = {
  ...ModalState,
  ...ReduxCardModalDispatchToPropsType,
  ...ReduxCardModalReducerStateType,
};

export type CardModalStateType = {
  authority?: NETGEM_API_V8_AUTHENT_REALM,
  backgroundImageUrl: string | null,
  broadcastStatus: BroadcastStatus,
  canBePlayed: boolean,
  channelImageUrl: string | null,
  contentType: ItemContent,
  continuousWatching: ContinuousWatching | null,
  followedPictoClassName: string,
  gameLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
  imageDisplayType: ImageDisplayType,
  isDeletable: boolean,
  isFollowed: ?boolean,
  isLiveRecording: boolean,
  isSeries: boolean,
  isWaitingForDeleteConfirmation: boolean,
  isWaitingForRecordConfirmation: boolean,
  parentalControlState: ParentalControlItemState,
  portraitImageUrl: ?string,
  previewCatchupScheduledEventId: ?string,
  programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  purchaseInfo: NETGEM_API_V8_PURCHASE_INFO | null,
  seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  slideshowImages: Array<string>,
  startoverItem: NETGEM_API_V8_FEED_ITEM | null,
  tvLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
  vodLocationsMetadata: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null,
  vodStatus: VOD_STATUS_TYPE | null,
};
